import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSeason } from "../components/SeasonContext";
import { TeamTableRow } from "../interfaces/Table";
import { PageHeader } from "./PageHeader";
import { TableFooter, Tooltip } from "@mui/material";

const SeasonTablePage: React.FC = () => {
  const { selectedSeason } = useSeason();
  const navigate = useNavigate();
  const [table, setTable] = useState<TeamTableRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch table for selected season using Django endpoint
  useEffect(() => {
    if (!selectedSeason) return;
    setLoading(true);
    const API_BASE_URL =
      process.env.NODE_ENV === "production"
        ? "https://api.wbl-dartliga.de/api"
        : import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api";
    fetch(`${API_BASE_URL}/season/${selectedSeason}/table/`)
      .then((res) => {
        if (!res.ok) throw new Error("Fehler beim Laden der Tabelle");
        return res.json();
      })
      .then((data: TeamTableRow[]) => {
        setTable(data);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [selectedSeason]);

  return (
    <>
      <PageHeader title={"Saisontabelle"} withStatistiksNav />
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : table && table.length > 0 ? (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <Tooltip title="Rang">
                  <TableCell sx={{ fontWeight: 700 }}>R</TableCell>
                </Tooltip>
                <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>

                <Tooltip title="Anzahl Spiele">
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    #
                  </TableCell>
                </Tooltip>
                <Tooltip title="Punkte">
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    P
                  </TableCell>
                </Tooltip>
                <Tooltip title="Siege">
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    S
                  </TableCell>
                </Tooltip>
                <Tooltip title="Niederlagen">
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    N
                  </TableCell>
                </Tooltip>
                
                <Tooltip title="Mannschaftsspiele/Entscheidungsspiele">
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    M
                  </TableCell>
                </Tooltip>
                <Tooltip title="Satzverhältnis">
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    SV
                  </TableCell>
                </Tooltip>
              </TableRow>
            </TableHead>
            <TableBody>
              {table.map((row, idx) => (
                <TableRow key={row.team_id}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell
                    onClick={() => navigate("/teams/detail", { state: { id: row.team_id } })}
                    sx={{
                      cursor: "pointer",
                      color: "primary.main",
                      fontWeight: 600,
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {row.name}{" "}
                    {row.postponements > 0 && `(${row.postponements})`}
                  </TableCell>
                  <TableCell align="center">{row.total_games_played}</TableCell>
                  <TableCell align="center">{row.total_points}</TableCell>
                  <TableCell align="center">{row.total_wins}</TableCell>
                  <TableCell align="center">{row.total_losses}</TableCell>
                  <TableCell align="center">{row.deciders_played}</TableCell>
                  <TableCell align="center">
                    {row.total_score_win}:{row.total_score_loss}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableCell colSpan={8}>
                R = Rang; # = Anzahl gemachte Spiele; P = Punkte;  S = Siege; N =
                Niederlagen; M = Mannschaftsspiele; SV =
                Satzverhältnis
              </TableCell>
            </TableFooter>
          </Table>
        </TableContainer>
      ) : (
        <Typography>Keine Daten gefunden.</Typography>
      )}
    </>
  );
};

export default SeasonTablePage;
